import { PathfinderApiClient } from '@blade/pathfinder-api-client'
import { Result } from '@blade/std-lib'

import type {
  QuotePostRequestBody,
  QuoteResponseBody,
  HealthData,
  SwaggerSupportedDex,
} from '@blade/pathfinder-api-client'

export type PathfinderRoutingMode = 'best' | 'straight'

export type PathfinderSenderKind = 'helius' | '0slot' | 'custom'

export interface PathfinderQuoteParams {
  tokenIn: string
  tokenOut: string
  amountInRaw: bigint | number | string
  taker: string
  mode?: PathfinderRoutingMode
  allowedDexes?: Array<SwaggerSupportedDex>
  referralCode?: string
  senderKind?: PathfinderSenderKind
  priority?: number
  tip?: number
  slippageBps?: number
}

export interface PathfinderHealth {
  ok: boolean
  data: HealthData
}

export interface PathfinderErrorContext {
  url?: string
  method?: string
  requestBody?: unknown
  status?: number
  statusText?: string
  responseBody?: unknown
}

export class PathfinderError extends Error {
  public readonly context: PathfinderErrorContext

  constructor(
    message: string,
    public readonly cause?: unknown,
    context: PathfinderErrorContext = {},
  ) {
    super(message)
    this.name = 'PathfinderError'
    this.context = context
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      context: this.context,
      cause:
        this.cause instanceof Error
          ? {
              name: this.cause.name,
              message: this.cause.message,
              stack: this.cause.stack,
            }
          : this.cause,
    }
  }
}

export interface QuoteManyOptions {
  concurrency?: number
  /** TTL default 500ms ≈ 2× pathfinder graph rebuild interval; 0 disables cache. */
  cacheTtlMs?: number
  signal?: AbortSignal
}

export interface PathfinderSwapService {
  quote: (
    params: PathfinderQuoteParams,
  ) => Promise<Result.Result<QuoteResponseBody, PathfinderError>>
  quoteMany: (
    paramsList: ReadonlyArray<PathfinderQuoteParams>,
    options?: QuoteManyOptions,
  ) => Promise<Array<Result.Result<QuoteResponseBody, PathfinderError>>>
  health: () => Promise<Result.Result<PathfinderHealth, PathfinderError>>
  invalidate: (params: PathfinderQuoteParams) => void
  invalidateAll: () => void
}

const buildDedupeKey = (p: PathfinderQuoteParams): string => {
  const dexes = p.allowedDexes ? [...p.allowedDexes].sort().join(',') : ''
  return [
    p.tokenIn,
    p.tokenOut,
    String(p.amountInRaw),
    p.taker,
    p.mode ?? '',
    dexes,
    p.referralCode ?? '',
    p.senderKind ?? '',
    p.priority ?? '',
    p.tip ?? '',
    p.slippageBps ?? '',
  ].join('|')
}

const toAmountIn = (raw: bigint | number | string): number => {
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw) || raw < 0) {
      throw new PathfinderError(`invalid amountIn: ${raw}`)
    }
    return Math.trunc(raw)
  }
  if (typeof raw === 'bigint') {
    if (raw < 0n) {
      throw new PathfinderError(`negative amountIn: ${raw}`)
    }
    if (raw > BigInt(Number.MAX_SAFE_INTEGER)) {
      throw new PathfinderError(
        `amountIn ${raw} exceeds Number.MAX_SAFE_INTEGER`,
      )
    }
    return Number(raw)
  }
  if (!/^\d+$/.test(raw)) {
    throw new PathfinderError(
      `amountIn must be a non-negative integer string, got: ${raw}`,
    )
  }
  const asBig = BigInt(raw)
  if (asBig > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new PathfinderError(`amountIn ${raw} exceeds Number.MAX_SAFE_INTEGER`)
  }
  return Number(asBig)
}

const buildBody = (p: PathfinderQuoteParams): QuotePostRequestBody => {
  const body: QuotePostRequestBody = {
    token_in: p.tokenIn,
    token_out: p.tokenOut,
    amount_in: toAmountIn(p.amountInRaw),
    taker: p.taker,
  }

  if (p.allowedDexes && p.allowedDexes.length > 0) {
    body.allowed_dexes = p.allowedDexes
  }

  if (p.mode === 'straight') {
    body.max_hops = 1
    body.allow_split = false
  }

  if (p.referralCode && p.referralCode.length > 0) {
    body.referral_code = p.referralCode
  }

  if (p.senderKind) {
    body.sender_type = p.senderKind
  }

  if (p.priority !== undefined && p.priority > 0) {
    body.priority = p.priority
  }

  if (p.tip !== undefined && p.tip > 0) {
    body.tip = p.tip
  }

  if (p.slippageBps !== undefined && p.slippageBps > 0) {
    body.slippage = p.slippageBps
  }

  return body
}

export const createPathfinderSwapService = (config: {
  baseUrl: string
  fetch?: typeof fetch
}): PathfinderSwapService => {
  if (!config.baseUrl) {
    throw new PathfinderError('PathfinderSwapService: baseUrl is required')
  }

  const client = new PathfinderApiClient({
    baseUrl: config.baseUrl,
    ...(config.fetch ? { customFetch: config.fetch } : {}),
  })

  const quoteUrl = `${config.baseUrl.replace(/\/$/, '')}/v1/quote`

  const quote: PathfinderSwapService['quote'] = async (params) => {
    let body: QuotePostRequestBody | undefined
    try {
      body = buildBody(params)
      const response = await client.pathfinderd.quotePost(body, {
        format: 'json',
      })
      if (!response.ok) {
        const rawError: unknown = response.error
        const errBody = (() => {
          if (typeof rawError === 'string' && rawError.length > 0) {
            return rawError
          }
          if (rawError && typeof rawError === 'object') {
            try {
              return JSON.stringify(rawError)
            } catch {
              return response.statusText
            }
          }
          return response.statusText
        })()
        return Result.fail(
          new PathfinderError(
            `pathfinder /v1/quote ${response.status}: ${errBody}`,
            response.error,
            {
              url: quoteUrl,
              method: 'POST',
              requestBody: body,
              status: response.status,
              statusText: response.statusText,
              responseBody: response.error,
            },
          ),
        )
      }
      return Result.succeed(response.data)
    } catch (error) {
      return Result.fail(
        new PathfinderError(
          error instanceof Error ? error.message : 'pathfinder quote failed',
          error,
          { url: quoteUrl, method: 'POST', requestBody: body },
        ),
      )
    }
  }

  const health: PathfinderSwapService['health'] = async () => {
    const url = `${config.baseUrl.replace(/\/$/, '')}/health`
    try {
      const response = await client.pathfinderd.health({ format: 'json' })
      return Result.succeed({ ok: response.ok, data: response.data })
    } catch (error) {
      return Result.fail(
        new PathfinderError(
          error instanceof Error ? error.message : 'pathfinder health failed',
          error,
          { url, method: 'GET' },
        ),
      )
    }
  }

  type CachedQuote = {
    result: Result.Result<QuoteResponseBody, PathfinderError>
    expiresAt: number
  }
  const cache = new Map<string, CachedQuote>()

  const quoteMany: PathfinderSwapService['quoteMany'] = async (
    paramsList,
    options = {},
  ) => {
    const concurrency = options.concurrency ?? 8
    const ttlMs = options.cacheTtlMs ?? 500
    const signal = options.signal
    const inFlight = new Map<
      string,
      Promise<Result.Result<QuoteResponseBody, PathfinderError>>
    >()

    const abortedResult = (): Result.Result<
      QuoteResponseBody,
      PathfinderError
    > =>
      Result.fail(
        new PathfinderError('pathfinder quoteMany aborted', signal?.reason),
      )

    const resolveOne = (
      params: PathfinderQuoteParams,
    ): Promise<Result.Result<QuoteResponseBody, PathfinderError>> => {
      if (signal?.aborted) {
        return Promise.resolve(abortedResult())
      }
      const key = buildDedupeKey(params)
      const now = Date.now()

      const cached = cache.get(key)
      if (cached) {
        if (cached.expiresAt > now) {
          return Promise.resolve(cached.result)
        }
        cache.delete(key)
      }

      const pending = inFlight.get(key)
      if (pending) {
        return pending
      }

      const promise = (async () => {
        const result = await quote(params)
        if (ttlMs > 0 && Result.isSuccess(result)) {
          cache.set(key, { result, expiresAt: Date.now() + ttlMs })
        }
        return result
      })()
      inFlight.set(key, promise)
      return promise
    }

    const effectiveConcurrency =
      !Number.isFinite(concurrency) || concurrency <= 0
        ? paramsList.length
        : Math.min(concurrency, paramsList.length)

    const results = Array.from<
      Result.Result<QuoteResponseBody, PathfinderError>
    >({
      length: paramsList.length,
    })
    let cursor = 0
    const workers = Array.from({ length: effectiveConcurrency }, async () => {
      while (cursor < paramsList.length) {
        const idx = cursor++
        if (signal?.aborted) {
          results[idx] = abortedResult()
          continue
        }
        results[idx] = await resolveOne(paramsList[idx]!)
      }
    })
    await Promise.all(workers)
    return results
  }

  const invalidate: PathfinderSwapService['invalidate'] = (params) => {
    cache.delete(buildDedupeKey(params))
  }

  const invalidateAll: PathfinderSwapService['invalidateAll'] = () => {
    cache.clear()
  }

  return { quote, quoteMany, health, invalidate, invalidateAll }
}

export const pickBestVariant = (
  body: QuoteResponseBody,
): QuoteResponseBody['variants'][number] | undefined => {
  if (body.variants.length === 0) {
    return undefined
  }
  return [...body.variants].sort((a, b) => {
    if (a.effective_price !== b.effective_price) {
      return a.effective_price - b.effective_price
    }
    return b.total_amount_out - a.total_amount_out
  })[0]
}

export const summarisePath = (
  variant: QuoteResponseBody['variants'][number],
): string => {
  const tokens = variant.routes[0]?.path_tokens.slice(0, 4)
  const dexes = [
    ...new Set(
      variant.routes.flatMap((r) =>
        r.hops.map((h) => h.provider ?? h.fixed_pool_family),
      ),
    ),
  ]
  const pathLabel = tokens?.join(' → ') ?? '?'
  const dexLabel = dexes.length > 0 ? dexes.join(' + ') : '?'
  return `${pathLabel} via ${dexLabel}`
}
